import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Translate } from 'react-localize-redux';
import Label from '../../components/foundations/Label';
import ProgressBar from '../../components/molecules/ProgressBar';
import { WarningMessage } from '../../components/widgets';
import withTranslate from '../../containers/widgets/withTranslate';
import { DATA_TEST_ID_ORG_PLAN_PROGRESS, PACING_TYPES, WARNING_TYPES } from '../consts';
import {
    calcProgressPercentage,
    calcProgressSectionsPercentages,
    calculateColorByPacingPercent,
    calculateWarningVariables,
} from '../utils';
import Card from './Card';
import css from './PacingCard.css';

const pacingTypesDict = {
    [PACING_TYPES.AD_SPEND]: {
        title: 'STATIC.PAGES.ACCOUNT_PLAN.PACING_CARD.TITLES.AD_SPEND',
        isMoney: true,
        tracked: 'STATIC.PAGES.ACCOUNT_PLAN.PACING_CARD.DESCRIPTION.AD_SPEND',
        warningValueType: 'USD',
    },
    [PACING_TYPES.CONVERSIONS]: {
        title: 'STATIC.PAGES.ACCOUNT_PLAN.PACING_CARD.TITLES.CONVERSIONS',
        isMoney: false,
        tracked: 'STATIC.PAGES.ACCOUNT_PLAN.PACING_CARD.DESCRIPTION.CONVERSIONS',
        warningValueType: 'conversions',
    },
};

function PacingCard({
    type,
    totalValue,
    progressSections = [],
    maxValue,
    minValue = 0,
    expiresInDays = 365,
    isFreeTier = false,
    isPaying = false,
    pacingTextWrapper = () => '',
    translate = () => '',
}) {
    const [fillcolor, setFillColor] = useState('var(--gray700)');
    const [highlightedId, setHighlightedId] = useState(null);
    const [warningType, setWarningType] = useState(WARNING_TYPES.EXTERNAL_LINK);
    const [warningMessage, setWarningMessage] = useState('');
    const { title, isMoney, tracked, warningValueType } = pacingTypesDict[type];

    const totalPercent = useMemo(() => calcProgressPercentage(totalValue, maxValue), [totalValue, maxValue]);
    const legendColor = useMemo(() => (type === PACING_TYPES.CONVERSIONS ? 'var(--gray700)' : fillcolor), [
        type,
        fillcolor,
    ]);
    const relevantProgressSections = progressSections.filter(({ value }) => value > 0);
    const progressSectionsPercentages = calcProgressSectionsPercentages(
        relevantProgressSections.map(({ value }) => value),
        maxValue
    );
    const breakdownProgressSections = relevantProgressSections.filter(({ titleKey }) => titleKey);

    useEffect(() => {
        setFillColor(calculateColorByPacingPercent(totalPercent, isFreeTier));
        const warning = calculateWarningVariables(isFreeTier, isPaying, expiresInDays, totalPercent);
        setWarningMessage(warning?.message || '');
        setWarningType(warning?.type || WARNING_TYPES.EXTERNAL_LINK);
    }, [isPaying, totalPercent, expiresInDays, isFreeTier]);

    const getFormattedValue = value => `${isMoney ? '$' : ''}${value.toLocaleString()} ${isMoney ? 'USD' : ''}`;

    const renderLegend = progressPercentage =>
        progressPercentage === 0 ? (
            `${progressPercentage}%`
        ) : (
            <Translate
                id={`STATIC.PAGES.ACCOUNT_PLAN.${
                    type === PACING_TYPES.CONVERSIONS ? 'TOTAL_CONVERSIONS' : 'PERCENT_USED'
                }`}
                data={{ percent: progressPercentage }}
            />
        );

    return (
        <Card
            dataTestId={DATA_TEST_ID_ORG_PLAN_PROGRESS}
            cardClassName={classNames({
                [css.attention]: warningType === WARNING_TYPES.ERROR,
            })}
            contentClassName={css.content}
            header={<Label className={css.title} text={title} />}
            style={{ '--pacing-bar-header-color': fillcolor }}
        >
            <div className={css.pacingContainer}>
                <div className={css.pacingBarHeaderWrapper}>
                    <Label className={css.pacingBarHeader} text={getFormattedValue(totalValue)} />
                    <Label
                        type="p"
                        text={` / ${
                            maxValue === Infinity
                                ? translate('STATIC.PAGES.ACCOUNT_PLAN.UNLIMITED')
                                : getFormattedValue(maxValue)
                        }`}
                    />
                </div>
                <div className={css.pacingBarInfo}>
                    <Label
                        type="sideLabel"
                        className={css.pacingTotalInfo}
                        text={pacingTextWrapper(tracked)}
                        tooltip={type === PACING_TYPES.CONVERSIONS && 'STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PACING_CARD'}
                    />
                    {breakdownProgressSections.length > 1 && (
                        <div className={css.breakdown}>
                            {breakdownProgressSections.map(({ id, titleKey, value, breakdownTooltipKey }) => (
                                <Label
                                    key={id}
                                    type="p"
                                    text={titleKey}
                                    textReplace={{
                                        value: Math.round(value).toLocaleString(),
                                    }}
                                    tooltip={breakdownTooltipKey}
                                    className={css.label}
                                    onMouseEnter={() => setHighlightedId(id)}
                                    onMouseLeave={() => setHighlightedId(null)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <ProgressBar
                progressSections={relevantProgressSections.map(({ id, value, progressBarTooltipKey }, index) => ({
                    id,
                    value,
                    tooltipKey: progressBarTooltipKey,
                    percentage: progressSectionsPercentages[index],
                }))}
                barHeight="16px"
                barWidth="100%"
                containerColor="var(--gray200)"
                fillColor={fillcolor}
                legendColor={legendColor}
                showLegend={maxValue !== Infinity}
                renderLegend={renderLegend}
                range={[minValue, maxValue]}
                valueType={isMoney ? 'money' : 'number'}
                highlightedId={highlightedId}
            />
            {warningMessage && (
                <WarningMessage
                    className={css.warningMessage}
                    type={warningType}
                    message={warningMessage}
                    showIcon={false}
                    showTypeIcon
                    showSpacer={false}
                    textReplace={{
                        usageLeft: Math.abs(maxValue - totalValue).toLocaleString(),
                        maxValue: Math.abs(maxValue).toLocaleString(),
                        expiresInDays: Math.abs(expiresInDays).toLocaleString(),
                        valueType: warningValueType,
                    }}
                />
            )}
        </Card>
    );
}

PacingCard.propTypes = {
    type: PropTypes.oneOf(Object.values(PACING_TYPES)).isRequired,
    totalValue: PropTypes.number.isRequired,
    progressSections: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.number.isRequired,
            id: PropTypes.string,
            titleKey: PropTypes.string,
            breakdownTooltipKey: PropTypes.string,
            progressBarTooltipKey: PropTypes.string,
        })
    ),
    maxValue: PropTypes.number.isRequired,
    minValue: PropTypes.number,
    expiresInDays: PropTypes.number,
    isFreeTier: PropTypes.bool,
    isPaying: PropTypes.bool,
    pacingTextWrapper: PropTypes.func,
    translate: PropTypes.func,
};

export default withTranslate(PacingCard);
